
import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {
    Customer,
    CustomerId, CustomerProp, CustomerRequest, CustomerRequestProp
} from "../../models/customers/Customer";
import { CustomerModule } from "../../models/customers/CustomerModule";
import {  ofType, StateObservable } from "redux-observable";
import { switchMap, map, catchError } from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {Nullable} from "../../models/common/Nullable";
import {Identifier, makeNumericId} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";
import {fetchCustomerOffices} from "./CustomerOfficeActions";
import {fetchCustomerContacts} from "./CustomerContactActions";

/**
 * base API path to interact with project roles
 */
function apiPath( id: Nullable<number> = null ): string {
    return APP_CONFIG.baseWeb + "customers/"+ ( id !== null ? id : '');
}

/** union of all project role actions types */
export type CustomerActionTypes = FetchCustomersActionTypes
    | FetchCustomerByIdActionTypes
    | CreateCustomerActionTypes
    | UpdateCustomerActionTypes
    | DeleteCustomerActionTypes
    | RestoreCustomerActionTypes

/**
 * FETCH CUSTOMERS BY USER VIEW SETTINGS
 *
 * user view settings will automatically be retrieved from redux store
 */
// TYPESCRIPT REDUX ACTION TYPES
export interface FetchCustomersAction extends ViewIdentifiable, ReduxAction< typeof FETCH_CUSTOMERS > {}
export interface FetchCustomersSuccessAction extends ViewIdentifiable, ReduxAction<typeof FETCH_CUSTOMERS_SUCCESS > {
    payload: ApiReturnType< Customer[] >
}
export interface FetchCustomersErrorAction extends ViewIdentifiable, ErrorAction< typeof FETCH_CUSTOMERS_ERROR >{}

export type FetchCustomersActionTypes = FetchCustomersAction
    | FetchCustomersErrorAction
    | FetchCustomersSuccessAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_CUSTOMERS = "FETCH_CUSTOMERS";
export const FETCH_CUSTOMERS_SUCCESS = "FETCH_CUSTOMERS_SUCCESS";
export const FETCH_CUSTOMERS_ERROR = "FETCH_CUSTOMERS_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function fetchCustomers( viewIdentifier: string ) : FetchCustomersAction {
    return { type: FETCH_CUSTOMERS, viewIdentifier };
}
export function fetchCustomersSuccess(
    viewIdentifier: string,
    payload: ApiReturnType< Customer[] >
) : FetchCustomersSuccessAction
{
    return { type: FETCH_CUSTOMERS_SUCCESS, viewIdentifier, payload };
}
export function fetchCustomersError( viewIdentifier: string, error: Error ): FetchCustomersErrorAction {
    return { type: FETCH_CUSTOMERS_ERROR, viewIdentifier, error };
}

// REDUX OBSERVABLE EPIC
export function fetchCustomersEpic(
    action$: Observable< FetchCustomersAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_CUSTOMERS ),
        switchMap( action => {
            const request = getUserViewSettingsApiRequest(
                apiPath(),
                action.viewIdentifier,
                store$
            );

            return ajax( APP_CONFIG.getRequestObject(request.path) ).pipe(
                map( response => fetchCustomersSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType<Customer[]>
                ) ),
                catchError(error => of(
                    fetchCustomersError(action.viewIdentifier, error),
                    eventNotificationError(error, store$, action.viewIdentifier, "fetchCustomers")
                ))
            );

        })
    );
}

/**
 * FETCH SINGLE CUSTOMER
 */
export interface FetchCustomerByIdAction extends CustomerId, ReduxAction< typeof FETCH_CUSTOMER_BY_ID> {

}
export interface FetchCustomerByIdSuccessAction extends CustomerProp, ReduxAction< typeof FETCH_CUSTOMER_BY_ID_SUCCESS> {

}
export interface FetchCustomerByIdErrorAction extends CustomerId, ErrorAction< typeof FETCH_CUSTOMER_BY_ID_ERROR> {

}
//union of action to fetch by id
export type FetchCustomerByIdActionTypes = FetchCustomerByIdAction
    | FetchCustomerByIdSuccessAction
    | FetchCustomerByIdErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_CUSTOMER_BY_ID = "FETCH_CUSTOMER_BY_ID";
export const FETCH_CUSTOMER_BY_ID_SUCCESS = "FETCH_CUSTOMER_BY_ID_SUCCESS";
export const FETCH_CUSTOMER_BY_ID_ERROR = "FETCH_CUSTOMER_BY_ID_ERROR";

//Redux action accessors function
export function fetchCustomerById( customerId: Identifier<number> ): FetchCustomerByIdAction {
    return { type: FETCH_CUSTOMER_BY_ID, customerId };
}
export function fetchCustomerByIdSuccess( customer: Customer ): FetchCustomerByIdSuccessAction {
    return { type: FETCH_CUSTOMER_BY_ID_SUCCESS, customer };
}
export function fetchCustomerByIdError( error: any, customerId: Identifier<number> ): FetchCustomerByIdErrorAction {
    return { type: FETCH_CUSTOMER_BY_ID_ERROR, error, customerId };
}

//redux observable epic as bridge to rxjs
export function fetchCustomerByIdEpic(
    action$: Observable< FetchCustomerByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_CUSTOMER_BY_ID ),
        switchMap( action => {
                return ajax(
                    APP_CONFIG.getRequestObject( apiPath( action.customerId.rawValue ) )
                ).pipe(
                    map( response => fetchCustomerByIdSuccess( response.response as Customer ) ),
                    catchError( error => of(
                        fetchCustomerByIdError( error, action.customerId ),
                        eventNotificationError( error, store$, CustomerModule.identifier, "fetchCustomerById" )
                    ) )
                )
            }
        )
    );
}

/**
 * MUTATING REQUESTS
 */

/**
 * api model does not accept null for offices and contacts for create and update, null will be changed to empty array
 *
 */
function toApiFormat( customerRequest: CustomerRequest ) {
    return {
        ...customerRequest,
        offices: customerRequest.offices !== null ? customerRequest.offices : [],
        contacts: customerRequest.contacts !== null ? customerRequest.contacts : []
    };
}

/**
 * CREATE CUSTOMER
 */
export interface CreateCustomerAction extends CustomerRequestProp, ReduxAction<typeof CREATE_CUSTOMER> {

}
export interface CreateCustomerSuccessAction extends CustomerId, ReduxAction<typeof CREATE_CUSTOMER_SUCCESS> {

}
export interface CreateCustomerErrorAction extends ErrorAction<typeof CREATE_CUSTOMER_ERROR> {

}
// typescript union type of create actions
export type CreateCustomerActionTypes = CreateCustomerAction
    | CreateCustomerSuccessAction
    | CreateCustomerErrorAction

//redux action constants
export const CREATE_CUSTOMER = "CREATE_CUSTOMER";
export const CREATE_CUSTOMER_SUCCESS = "CREATE_CUSTOMER_SUCCESS";
export const CREATE_CUSTOMER_ERROR = "CREATE_CUSTOMER_ERROR";

//redux action accessors functions
export function createCustomer( customerReq: CustomerRequest ): CreateCustomerAction {
    return { type: CREATE_CUSTOMER, customerReq };
}
export function createCustomerSuccess( customerId: Identifier<number> ): CreateCustomerSuccessAction {
    return { type: CREATE_CUSTOMER_SUCCESS, customerId };
}
export function createCustomerError( error: any ): CreateCustomerErrorAction {
    return { type: CREATE_CUSTOMER_ERROR, error };
}

//redux observable rxjs bridge
export function createCustomerEpic(
    action$: Observable< CreateCustomerAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_CUSTOMER ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.post,
                    toApiFormat( action.customerReq )
                )
            ).pipe(
                switchMap( response => of(
                    createCustomerSuccess( ( response.response as CustomerId ).customerId ),
                    fetchCustomers( CustomerModule.identifier ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.customers.routes.main.path,
                        CustomerModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createCustomerError( error ),
                    eventNotificationError( error, store$, CustomerModule.identifier, "createCustomer" )
                ))
            )
        )
    )
}

/**
 * UPDATE CUSTOMER
 */
export interface UpdateCustomerAction extends CustomerRequestProp, ReduxAction<typeof UPDATE_CUSTOMER> {

}
export interface UpdateCustomerSuccessAction extends CustomerId, ReduxAction<typeof UPDATE_CUSTOMER_SUCCESS> {

}
export interface UpdateCustomerErrorAction extends ErrorAction<typeof UPDATE_CUSTOMER_ERROR> {

}
// typescript union type of update actions
export type UpdateCustomerActionTypes = UpdateCustomerAction
    | UpdateCustomerSuccessAction
    | UpdateCustomerErrorAction

//redux action constants
export const UPDATE_CUSTOMER = "UPDATE_CUSTOMER";
export const UPDATE_CUSTOMER_SUCCESS = "UPDATE_CUSTOMER_SUCCESS";
export const UPDATE_CUSTOMER_ERROR = "UPDATE_CUSTOMER_ERROR";

//redux action accessors functions
export function updateCustomer( customerReq: CustomerRequest ): UpdateCustomerAction {
    return { type: UPDATE_CUSTOMER, customerReq };
}
export function updateCustomerSuccess( customerId: Identifier<number> ): UpdateCustomerSuccessAction {
    return { type: UPDATE_CUSTOMER_SUCCESS, customerId };
}
export function updateCustomerError( error: any ): UpdateCustomerErrorAction {
    return { type: UPDATE_CUSTOMER_ERROR, error };
}

//redux observable rxjs bridge
export function updateCustomerEpic(
    action$: Observable< UpdateCustomerAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_CUSTOMER ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.customerReq.customerId ),
                    RouteMethods.put,
                    toApiFormat( action.customerReq )
                )
            ).pipe(
                switchMap( response => of(
                    updateCustomerSuccess( ( response.response as CustomerId ).customerId ),
                    fetchCustomers( CustomerModule.identifier ),
                    fetchCustomerOffices(
                        makeNumericId( action.customerReq.customerId),
                        CustomerModule.identifier
                    ),
                    fetchCustomerContacts(
                        makeNumericId( action.customerReq.customerId ),
                        CustomerModule.identifier
                    ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.customers.routes.main.path,
                        CustomerModule.identifier
                    )
                ) ),
                catchError( error => of(
                    updateCustomerError( error ),
                    eventNotificationError( error, store$, CustomerModule.identifier, "updateCustomer" )
                ))
            )
        )
    )
}

/**
 * DELETE CUSTOMER
 */
export interface DeleteCustomerAction extends CustomerId, ReduxAction<typeof DELETE_CUSTOMER> {

}
export interface DeleteCustomerSuccessAction extends CustomerId, ReduxAction<typeof DELETE_CUSTOMER_SUCCESS> {

}
export interface DeleteCustomerErrorAction extends ErrorAction<typeof DELETE_CUSTOMER_ERROR> {

}
// typescript union type of delete actions
export type DeleteCustomerActionTypes = DeleteCustomerAction
    | DeleteCustomerSuccessAction
    | DeleteCustomerErrorAction

//redux action constants
export const DELETE_CUSTOMER = "DELETE_CUSTOMER";
export const DELETE_CUSTOMER_SUCCESS = "DELETE_CUSTOMER_SUCCESS";
export const DELETE_CUSTOMER_ERROR = "DELETE_CUSTOMER_ERROR";

//redux action accessors functions
export function deleteCustomer( customerId: Identifier<number>  ): DeleteCustomerAction {
    return { type: DELETE_CUSTOMER, customerId };
}
export function deleteCustomerSuccess( customerId: Identifier<number> ): DeleteCustomerSuccessAction {
    return { type: DELETE_CUSTOMER_SUCCESS, customerId };
}
export function deleteCustomerError( error: any ): DeleteCustomerErrorAction {
    return { type: DELETE_CUSTOMER_ERROR, error };
}

//redux observable rxjs bridge
export function deleteCustomerEpic(
    action$: Observable< DeleteCustomerAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_CUSTOMER ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.customerId.rawValue ),
                    RouteMethods.delete
                )
            ).pipe(
                switchMap( _ => of(
                    deleteCustomerSuccess( action.customerId ),
                    eventNotificationSuccess(),
                    fetchCustomerById( action.customerId )
                ) ),
                catchError( error => of(
                    deleteCustomerError( error ),
                    eventNotificationError( error, store$, CustomerModule.identifier, "deleteCustomer" )
                ))
            )
        )
    )
}

/**
 * RESTORE CUSTOMER
 */
export interface RestoreCustomerAction extends CustomerId, ReduxAction<typeof RESTORE_CUSTOMER> {

}
export interface RestoreCustomerSuccessAction extends CustomerId, ReduxAction<typeof RESTORE_CUSTOMER_SUCCESS> {

}
export interface RestoreCustomerErrorAction extends ErrorAction<typeof RESTORE_CUSTOMER_ERROR> {

}
// typescript union type of restore actions
export type RestoreCustomerActionTypes = RestoreCustomerAction
    | RestoreCustomerSuccessAction
    | RestoreCustomerErrorAction

//redux action constants
export const RESTORE_CUSTOMER = "RESTORE_CUSTOMER";
export const RESTORE_CUSTOMER_SUCCESS = "RESTORE_CUSTOMER_SUCCESS";
export const RESTORE_CUSTOMER_ERROR = "RESTORE_CUSTOMER_ERROR";

//redux action accessors functions
export function restoreCustomer( customerId: Identifier<number>  ): RestoreCustomerAction {
    return { type: RESTORE_CUSTOMER, customerId };
}
export function restoreCustomerSuccess( customerId: Identifier<number> ): RestoreCustomerSuccessAction {
    return { type: RESTORE_CUSTOMER_SUCCESS, customerId };
}
export function restoreCustomerError( error: any ): RestoreCustomerErrorAction {
    return { type: RESTORE_CUSTOMER_ERROR, error };
}

//redux observable rxjs bridge
export function restoreCustomerEpic(
    action$: Observable< RestoreCustomerAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_CUSTOMER ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.customerId.rawValue ),
                    RouteMethods.patch
                )
            ).pipe(
                switchMap( _ => of(
                    restoreCustomerSuccess( action.customerId ),
                    eventNotificationSuccess(),
                    fetchCustomerById( action.customerId )
                ) ),
                catchError( error => of(
                    restoreCustomerError( error ),
                    eventNotificationError( error, store$, CustomerModule.identifier, "restoreCustomer" )
                ))
            )
        )
    )
}